import React from 'react'
import { Phone, PhoneOff, User } from 'lucide-react'
import type { CallerInfo } from '@/stores/callStore'

interface IncomingCallToastProps {
  callerInfo: CallerInfo | null
  onAccept: () => void
  onReject: () => void
}

/**
 * Desktop corner toast for incoming calls — floats top-right with more detail.
 * On mobile this is hidden; MobileIncomingCallOverlay takes over.
 * Under 88px tall on mobile (per spec); desktop has more breathing room.
 */
export function IncomingCallToast({ callerInfo, onAccept, onReject }: IncomingCallToastProps) {
  if (!callerInfo) return null

  return (
    <div className="animate-fade-up">
      <div
        className="ds-tile"
        style={{
          // An incoming call needs you now — white edge, not a pastel.
          borderColor: 'rgba(255,255,255,0.30)',
          padding: '14px 16px',
        }}
      >
        {/* Caller info row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--r-pill)',
              background: 'rgba(255,255,255,0.06)',
              border: '0.5px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <User style={{ width: 18, height: 18, color: 'rgba(255,255,255,0.60)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: '#FFFFFF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginBottom: 2,
              }}
            >
              {callerInfo.name || 'Unknown caller'}
            </p>
            <p
              style={{
                fontSize: 13,
                color: 'var(--midnight-text3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {callerInfo.number}
            </p>
          </div>
        </div>

        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--midnight-text3)',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          Incoming call
        </p>

        {/* Accept / Decline buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Decline */}
          <button
            type="button"
            className="btn-danger"
            onClick={onReject}
            aria-label="Decline call"
            style={{ flex: 1, height: 44, fontSize: 13, padding: '0 14px' }}
          >
            <PhoneOff style={{ width: 15, height: 15 }} />
            Decline
          </button>

          {/* Accept */}
          <button
            type="button"
            className="btn-primary"
            onClick={onAccept}
            aria-label="Accept call"
            style={{ flex: 1, height: 44, fontSize: 13, padding: '0 14px' }}
          >
            <Phone style={{ width: 15, height: 15 }} />
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
